import React from 'react'
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
} from '@mui/material'
import Grid2 from '@mui/material/Unstable_Grid2' // Grid version 2
import { Stack } from '@mui/material'
import Header from './Header'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import BomTable from './BomTable.js'
import BomGridTotal from './BomGridTotal.js'
import BomGridButtonRow from './BomGridButtonRow.js'
import GridTitleRow from './GridTitleRow.js'
import fenceCalc from '../utilities/fenceCalc'
import * as Money from '../utilities/money'

function getSummaryTypography(fenceSummaryRpt) {
    const typoAr = fenceSummaryRpt.split('\n')

    return typoAr.map((item, i) => {
        return <Typography key={i}>{item}</Typography>
    })
}

function getOrderTotal(bomLineItems) {
    const orderTotalNum = bomLineItems.reduce((acc, { itemQty, sugRetail }) => {
        return acc + itemQty * Money.strToIntPennies(sugRetail)
    }, 0)

    return orderTotalNum
}
function getStorageArObj({ bomLineItems }) {
    const storageArObj = bomLineItems.map((item) => {
        const { productIDNum, itemQty: quantity } = item
        return { productIDNum, quantity }
    })
    return storageArObj
}

export default function BOMGrid({
    sessionObj,
    invList,
    isCAD,
    setIsCADCurrency,
}) {
    const [bomState, setBomState] = React.useState({
        bomLineItems: [],
        fenceSummaryRpt: '',
    })
    const [openSummaryDlg, setOpenSummaryDlg] = React.useState(false)
    const theme = useTheme()
    const isDesktopDisplay = useMediaQuery(theme.breakpoints.up('md'))

    // Tracks quantities the user has manually changed
    // Key: productIDNum, Value: edited quantity
    // useRef so it persists across re-renders without triggering them
    const userEditsRef = React.useRef({})

    React.useEffect(() => {
        let { materialsList, fenceSummaryRpt } = fenceCalc(sessionObj, invList)
        let bomLineItems = materialsList.map(
            ({
                productIDNum,
                href,
                productID,
                descriptionTitle,
                pkgQty,
                itemQty,
                srp,
                displayOrder,
            }) => {
                const sugRetail = Money.getMoneyFormat(srp)

                // If the user manually edited this item's qty, keep their value
                const editedQty = userEditsRef.current[productIDNum]
                const finalQty = editedQty !== undefined ? editedQty : itemQty

                return {
                    productIDNum,
                    href,
                    productID,
                    descriptionTitle,
                    pkgQty,
                    sugRetail,
                    itemQty: finalQty,
                    displayOrder,
                }
            }
        )
        setBomState({ bomLineItems, fenceSummaryRpt })
    }, [sessionObj, invList])

    const handleSaveToCart = () => {
        const storageArObj = getStorageArObj(bomState)
        //remove any non inventory items
        const filteredStoreageArObj = storageArObj.filter(
            ({ productIDNum }) => {
                return (
                    productIDNum.slice(0, 3) !== '999' &&
                    // Temporary: AS1-EB (productIDNum '404') is out of stock in
                    // Canada. Don't push it into the CAD cart. Notice near the
                    // BOM table tells the customer they'll need to source them
                    // separately. Remove this line when Canadian stock returns.
                    !(isCAD && productIDNum === '404')
                )
            }
        )

        if (window.fbq) {
            window.fbq('track', 'AddToCart')
        }

        const filteredStoreageArObj2 = filteredStoreageArObj.filter(
            ({ quantity }) => quantity
        )

        localStorage.setItem(
            'shopping-cart',
            JSON.stringify(filteredStoreageArObj2)
        )
        window.location.href = '/shop/cart'
    }

    const orderTotal = Money.intPenniesToStr(
        getOrderTotal(bomState.bomLineItems)
    )

    const handleInputChange = (
        { target: { value: itemQty } },
        { productIDNum: productIDNumChg }
    ) => {
        // Record that the user intentionally changed this quantity
        userEditsRef.current[productIDNumChg] = itemQty

        setBomState((bomState) => {
            let bomLineItems = bomState.bomLineItems.map((lineItem) => {
                if (lineItem.productIDNum === productIDNumChg) {
                    return { ...lineItem, itemQty }
                }
                return lineItem
            })

            return { ...bomState, bomLineItems }
        })
    }

    const handleValue = (productIDNumChg) => {
        const lineItem = bomState.bomLineItems.find(
            ({ productIDNum }) => productIDNum === productIDNumChg
        )
        return lineItem.itemQty || 0
    }

    function handleClose() {
        setOpenSummaryDlg(false)
    }

    let isLoaded =
        Array.isArray(bomState.bomLineItems) && bomState.bomLineItems.length

    return !isLoaded ? (
        <Stack
            direction={'row'}
            alignItems="center"
            justifyContent={'space-evenly'}
        >
            <CircularProgress sx={{ my: 10 }} />
            <Typography sx={{ my: 10, fontWeight: 500, fontSize: '1.3rem' }}>
                Loading Fencing Estimate
            </Typography>
            <CircularProgress sx={{ my: 10 }} />
            <Typography sx={{ my: 10, fontWeight: 500, fontSize: '1.3rem' }}>
                Loading Fencing Estimate
            </Typography>
            <CircularProgress sx={{ my: 10 }} />
        </Stack>
    ) : (
        <>
            <Grid2
                container
                columns={20}
                xs={20}
                sx={{
                    border: 2,
                    borderColor: 'primary.main',
                }}
            >
                <Box
                    sx={{
                        minWidth: '100%',
                    }}
                >
                    <GridTitleRow />

                    {isDesktopDisplay && <Header />}
                    <BomTable
                        bomLineItems={bomState.bomLineItems}
                        handleInputChange={handleInputChange}
                        handleValue={handleValue}
                        isDesktopDisplay={isDesktopDisplay}
                        invList={invList}
                    />

                    {/*
                    Canada-only stockout notice for AS1-EB (productIDNum '404').
                    Shows only when:
                        1. Currency is CAD
                        2. The current BOM actually includes short screws (T-post fences
                        don't need them, so no reason to warn those customers).
                    When Canadian stock returns, delete this whole block AND the
                    AS1-EB filter guard in handleSaveToCart above.
                    */}
                    {isCAD &&
                        bomState.bomLineItems.some(
                            ({ productIDNum, itemQty }) =>
                                productIDNum === '404' && itemQty > 0
                        ) && (
                            <Box
                                sx={{
                                    m: 2,
                                    p: 2,
                                    border: `1px solid rgba(185, 134, 71, 0.4)`,
                                    backgroundColor: 'rgba(185, 134, 71, 0.08)',
                                    borderRadius: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, mb: 0.5 }}
                                >
                                    AS1-EB (1&nbsp;inch insulator screws) —
                                    temporarily out of stock in Canada
                                </Typography>
                                <Typography variant="body2">
                                    Your estimate still shows the correct
                                    quantity of AS1-EB you&#39;ll need. They
                                    won&#39;t be added to your cart from this
                                    calculator. Please contact{' '}
                                    <a href="mailto:info@electrobraid.com">
                                        info@electrobraid.com
                                    </a>{' '}
                                    for an ETA or to arrange a substitute.
                                </Typography>
                            </Box>
                        )}

                    <BomGridTotal
                        isDesktopDisplay={isDesktopDisplay}
                        orderTotal={orderTotal}
                        bomLineItems={bomState.bomLineItems}
                        isCAD={isCAD}
                        setIsCADCurrency={setIsCADCurrency}
                    />
                    <BomGridButtonRow
                        setOpenSummaryDlg={setOpenSummaryDlg}
                        handleSaveToCart={handleSaveToCart}
                    />
                </Box>
            </Grid2>

            <Dialog open={openSummaryDlg}>
                <DialogTitle>Fence Summary</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: 'black' }}>
                        {getSummaryTypography(bomState.fenceSummaryRpt)}
                    </DialogContentText>
                    <DialogActions sx={{ marginTop: '1rem' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleClose}
                            autoFocus
                        >
                            Close
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    )
}
